import React, { useState, useEffect } from 'react';

interface Proveedor {
    clave_persona: string | number;
    id_encriptado: string;
    nombre_completo: string;
    rfc: string;
    clave_estatus: string;
    clave_estado: number | string;
    clave_ciudad: number | string;
}

interface Estado {
    clave_estado: number | string;
    clave_pais: number | string;
    estado: string;
}

interface Ciudad {
    clave_ciudad: number | string;
    ciudad: string;
    clave_estado: number | string;
}

const ProveedoresList: React.FC = () => {
    // --- Estados de Datos ---
    const [proveedores, setProveedores] = useState<Proveedor[]>([]);
    const [estados, setEstados] = useState<Estado[]>([]);
    const [ciudades, setCiudades] = useState<Ciudad[]>([]);

    // --- Estados de UI ---
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // --- Estados de Búsqueda y Filtrado Server-Side ---
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('ALL');
    const [selectedEstado, setSelectedEstado] = useState<string>('');
    const [selectedCiudad, setSelectedCiudad] = useState<string>('');

    // --- Estados de Paginación y Ordenamiento ---
    const [page, setPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(10);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [sortBy, setSortBy] = useState<string>('nombre');
    const [sortOrder, setSortOrder] = useState<string>('asc');

    useEffect(() => {
        fetchEstados();
    }, []);

    useEffect(() => {
        if (selectedEstado !== "") {
            fetchCiudades(selectedEstado);
        } else {
            setCiudades([]);
        }
        setSelectedCiudad('');
    }, [selectedEstado]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        fetchProveedores();
    }, [debouncedSearch, statusFilter, selectedEstado, selectedCiudad, page, limit, sortBy, sortOrder]);


    const fetchSeguro = async (url: string, options?: RequestInit) => {
        const response = await fetch(url, options);

        const data = await response.json();

        if (data && data.status === 'error' && data.message && data.message.includes('Sesión inválida')) {
            window.location.href = '/';
            throw new Error("Sesión expirada. Redirigiendo...");
        }
        return data;
    };

    const fetchProveedores = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const dbSortParam = `${sortBy}_${sortOrder}`;

            const result = await fetchSeguro('/sittApi/obtener_proveedores.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    searchTerm: debouncedSearch,
                    statusFilter,
                    state: selectedEstado,
                    city: selectedCiudad,
                    page,
                    limit,
                    sortBy: dbSortParam
                }),
            });

            if (result.status === 'success') {
                setProveedores(result.data || []);
                setTotalRecords(result.total || 0);
            } else {
                setError(result.message || 'Error al cargar los datos.');
            }
        } catch (err) {
            setError("Error de red al comunicarse con el servidor.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchEstados = async () => {
        try {
            const result = await fetchSeguro('/sittApi/obtener_estados.php');
            if (result.status === 'success') setEstados(result.data || []);
        } catch (err) {
            console.error("Error cargando estados", err);
        }
    };

    const fetchCiudades = async (idEstado: string) => {
        try {
            const result = await fetchSeguro("/api/catalogos/ciudades.php", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({
                    accion: "filtered",
                    idEstado: idEstado
                }),
            });
            setCiudades(result || []);
        } catch (err) {
            console.error("Error cargando ciudades", err);
        }
    };

    // --- Handlers de Interacción ---
    const handleSort = (column: string) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
        setPage(1);
    };

    const handleFilterChange = (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
        setter(value);
        setPage(1);
    };

    // --- Render Helpers ---
    const renderSortIcon = (column: string) => {
        if (sortBy !== column) return <i className="fas fa-sort text-muted ms-1 opacity-25"></i>;
        return sortOrder === 'asc' ?
            <i className="fas fa-sort-up text-primary ms-1"></i> :
            <i className="fas fa-sort-down text-primary ms-1"></i>;
    };

    const renderStatusBadge = (estatus: string) => {
        switch (estatus) {
            case 'PEND': return <span className="badge bg-warning bg-opacity-10 text-warning border border-warning" style={{ color: 'var(--value-warning-color)' }}>Nuevo a prueba</span>;
            case 'ACTI': return <span className="badge bg-success bg-opacity-10 text-success border border-success">Activo</span>;
            case 'REVI': return <span className="badge bg-warning bg-opacity-10 text-warning border border-warning" style={{ color: 'var(--value-warning-color)' }}>A prueba por servicio</span>;
            case 'BLOQ': return <span className="badge bg-warning bg-opacity-10 text-warning border border-warning" style={{ color: 'var(--value-warning-color)' }}>Bloqueado</span>;
            case 'CANC': return <span className="badge bg-danger bg-opacity-10 text-danger border border-danger">Eliminado</span>;
            default: return <span className="badge bg-secondary">{estatus}</span>;
        }
    };

    const totalPages = Math.ceil(totalRecords / limit) || 1;
    const maxVisiblePages = 5;

    const renderPaginationItems = () => {
        const pages = [];
        let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
        let endPage = startPage + maxVisiblePages - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        if (startPage > 1) {
            pages.push(
                <li key={1} className="page-item">
                    <button className="page-link" onClick={() => setPage(1)}>1</button>
                </li>
            );
            if (startPage > 2) {
                pages.push(
                    <li key="ellipsis-start" className="page-item disabled">
                        <span className="page-link border-0 text-muted">...</span>
                    </li>
                );
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${page === i ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setPage(i)} disabled={isLoading}>
                        {i}
                    </button>
                </li>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(
                    <li key="ellipsis-end" className="page-item disabled">
                        <span className="page-link border-0 text-muted">...</span>
                    </li>
                );
            }
            pages.push(
                <li key={totalPages} className="page-item">
                    <button className="page-link fw-bold" onClick={() => setPage(totalPages)} title="Última página">
                        {totalPages}
                    </button>
                </li>
            );
        }

        return pages;
    };

    return (
        <div className="card shadow-sm border-0 animate__animated animate__fadeIn">
            <div className="card-header border-bottom py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold">
                    <i className="fas fa-store text-primary me-2"></i> Directorio de Proveedores
                </h5>
                <a href="proveedores?accion=add" className="btn btn-primary btn-sm px-4 fw-bold shadow-sm">
                    <i className="fas fa-plus me-1"></i> Nuevo Proveedor
                </a>
            </div>

            <div className="card-body p-4">
                <div className="row g-3 mb-4">
                    <div className="col-md-12 col-lg-4">
                        <div className="input-group shadow-sm">
                            <span className="input-group-text border-end-0 text-primary">
                                <i className="fas fa-search"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control border-start-0 ps-0"
                                placeholder="Buscar por Nombre comercial o RFC..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="col-md-4 col-lg-2">
                        <select className="form-select shadow-sm" value={statusFilter} onChange={(e) => handleFilterChange(setStatusFilter, e.target.value)}>
                            <option value="ALL">Todos los estatus</option>
                            <option value="ACTI">Activos</option>
                            <option value="PEND">Nuevos a prueba</option>
                            <option value="BLOQ">Bloqueados</option>
                            <option value="CANC">Eliminados</option>
                        </select>
                    </div>

                    <div className="col-md-4 col-lg-2">
                        <select className="form-select shadow-sm" value={selectedEstado} onChange={(e) => handleFilterChange(setSelectedEstado, e.target.value)}>
                            <option value="">Todos los Estados</option>
                            {estados.map(e => (
                                <option key={e.clave_estado} value={e.clave_estado}>{e.estado}</option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-4 col-lg-3">
                        <select className="form-select shadow-sm" value={selectedCiudad} onChange={(e) => handleFilterChange(setSelectedCiudad, e.target.value)} disabled={!selectedEstado}>
                            <option value="">Todas las Ciudades</option>
                            {ciudades.map(c => (
                                <option key={c.clave_ciudad} value={c.clave_ciudad}>{c.ciudad}</option>
                            ))}
                        </select>
                    </div>

                    <div className="col-lg-1 text-end">
                        <button className="btn border text-muted w-100" onClick={fetchProveedores} title="Actualizar datos" disabled={isLoading}>
                            <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-danger border-0 shadow-sm">
                        <i className="fas fa-exclamation-triangle me-2"></i> {error}
                    </div>
                )}

                {/* --- TABLA DE RESULTADOS --- */}
                <div className="table-responsive rounded border shadow-sm mb-3" style={{ maxHeight: 'none' }}>
                    <table className="table table-hover align-middle mb-0">
                        <thead className=" text-secondary">
                            <tr>
                                <th className="ps-4 user-select-none" onClick={() => handleSort('nombre')} style={{ cursor: 'pointer' }} title="Ordenar por Nombre">
                                    Nombre / Razón Social {renderSortIcon('nombre')}
                                </th>
                                <th className="text-center user-select-none" onClick={() => handleSort('rfc')} style={{ cursor: 'pointer' }} title="Ordenar por RFC">
                                    RFC {renderSortIcon('rfc')}
                                </th>
                                <th className="text-center user-select-none" onClick={() => handleSort('estado')} style={{ cursor: 'pointer' }} title="Ordenar por Estado">
                                    Estado {renderSortIcon('estado')}
                                </th>
                                <th className="text-center user-select-none" onClick={() => handleSort('ciudad')} style={{ cursor: 'pointer' }} title="Ordenar por Ciudad">
                                    Ciudad {renderSortIcon('ciudad')}
                                </th>
                                <th className="text-center user-select-none" onClick={() => handleSort('estatus')} style={{ cursor: 'pointer' }} title="Ordenar por Estatus">
                                    Estatus {renderSortIcon('estatus')}
                                </th>
                                <th className="text-end pe-4">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-5 text-muted">
                                        <div className="spinner-border text-primary mb-2" role="status"></div>
                                        <p className="mb-0 small">Consultando base de datos...</p>
                                    </td>
                                </tr>
                            ) : proveedores.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-5 text-muted fst-italic">
                                        <i className="fas fa-search fa-2x mb-3"></i><br />
                                        No se encontraron proveedores con los filtros actuales.
                                    </td>
                                </tr>
                            ) : (
                                proveedores.map((prov) => {
                                    const estadoNombre = estados.find((e) => e.clave_estado === prov.clave_estado)?.estado || '';
                                    const ciudadNombre = ciudades.find((c) => c.clave_ciudad === prov.clave_ciudad)?.ciudad || '';

                                    return (
                                        <tr key={prov.clave_persona}>
                                            <td className="ps-4 fw-bold">{prov.nombre_completo}</td>
                                            <td className="text-center">
                                                <span className="badge border border-secondary font-monospace text-secondary">{prov.rfc || 'N/A'}</span>
                                            </td>
                                            <td className="text-center small">
                                                {estadoNombre || 'N/A'}
                                            </td>
                                            <td className="text-center small">
                                                {ciudadNombre && <span className="text-muted d-block">{ciudadNombre}</span>}
                                                {(!ciudadNombre) && 'N/A'}
                                            </td>
                                            <td className="text-center">{renderStatusBadge(prov.clave_estatus)}</td>
                                            <td className="text-end pe-4">
                                                <a href={`proveedores?accion=view&id=${prov.id_encriptado}`} className="btn btn-sm border border-secondary text-primary me-2 rounded-circle" title="Ver detalle">
                                                    <i className="far fa-eye"></i>
                                                </a>
                                                <a href={`proveedores?accion=edit&id=${prov.id_encriptado}`} className="btn btn-sm border border-secondary text-warning rounded-circle" title="Editar">
                                                    <i className="far fa-pencil-alt"></i>
                                                </a>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* --- CONTROLES DE PAGINACIÓN AVANZADA --- */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">

                    <div className="d-flex align-items-center mb-3 mb-md-0">
                        <span className="text-muted small me-2">Mostrar:</span>
                        <select
                            className="form-select form-select-sm shadow-sm w-auto"
                            value={limit}
                            onChange={(e) => {
                                setLimit(Number(e.target.value));
                                setPage(1);
                            }}
                            disabled={isLoading}
                        >
                            <option value="10">10 registros</option>
                            <option value="25">25 registros</option>
                            <option value="50">50 registros</option>
                            <option value="100">100 registros</option>
                        </select>
                        <span className="text-muted small ms-3 d-none d-sm-inline">
                            Total: <strong>{totalRecords}</strong> resultados.
                        </span>
                    </div>

                    {totalPages > 1 && (
                        <ul className="pagination pagination-sm mb-0 shadow-sm">

                            {/* Botón Anterior */}
                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1 || isLoading} title="Página anterior">
                                    <i className="fas fa-angle-left"></i>
                                </button>
                            </li>

                            {/* Renderizado de Páginas y Truncamiento */}
                            {renderPaginationItems()}

                            {/* Botón Siguiente */}
                            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages || isLoading} title="Página siguiente">
                                    <i className="fas fa-angle-right"></i>
                                </button>
                            </li>

                        </ul>
                    )}
                </div>

            </div>
        </div >
    );
};

export default ProveedoresList;